'use client'

import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'
import { IAuthSession } from '~/schemas/auth/authSchemas'
import { isApiError } from '~/schemas/errorSchemas'
import { expireSessionService, refreshSessionService } from '~/services/domain/auth/SessionService'

let pendingRefresh: Promise<IAuthSession | null> | null = null

/**
 * Uma renovação por vez para a aplicação toda (várias telas pedem ao mesmo tempo depois de uma edição). 400 é sessão
 * sem cliente: encerra e avisa pelo evento `sessionExpired` uma vez só; 401 já encerra pelo interceptor do ApiService.
 */
function refreshOnce(): Promise<IAuthSession | null> {
    if (!pendingRefresh) {
        pendingRefresh = refreshSessionService()
            .catch((error) => {
                if (isApiError(error) && error.statusCode === 400) expireSessionService()
                throw error
            })
            .finally(() => {
                pendingRefresh = null
            })
    }
    return pendingRefresh
}

/**
 * Renova a sessão pelo update-token (token, cliente e carrinho): recompõe o cache quando há token sem cliente e
 * atualiza depois de editar dados ou endereço. `refreshSession` devolve a sessão, ou `null` se um login ou logout
 * aconteceu durante a chamada; rejeita com o ApiError (outras falhas mantêm o estado).
 */
export function useSessionRefresh() {
    const refreshMutation = useMutation({ mutationFn: refreshOnce })

    const refreshSession = useCallback(() => refreshMutation.mutateAsync(), [refreshMutation.mutateAsync])

    return { refreshSession, isRefreshing: refreshMutation.isPending }
}
